import React, { useEffect, useState } from 'react'
import { Box, Paper, Skeleton, Theme, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import ImageComponent from './ImageComponent'
import MediaCropperComponent from './MediaCropperComponent'
import { MediaFileMetadata } from '@3m5/sm_publish-frontend-api'
import VideoComponent from './VideoComponent'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minWidth: '300px',
    width: '100%',
  },
  imgDesc: {
    padding: theme.spacing(2),
  },
  image: {
    height: theme.spacing(42),
    width: '100%',
    objectFit: 'cover',
    borderTopLeftRadius: '2%',
    borderTopRightRadius: '2%',
  },
  actionItem: {
    marginLeft: theme.spacing(1),
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
}))

type IProps = {
  file?: MediaFileMetadata | string
  temporaryMediaFile?: MediaFileMetadata
  onlyUploadButton?: boolean
  setMediaFile?: (file: string | undefined, fileId?: string, type?: string) => void // not called on Upload
  getMediaData?: (params: any) => void // called on upload
  isUploaded?: boolean
  allowVideoUpload?: boolean
  allowImageUpload?: boolean
  containerWidth?: number
  containerHeight?: number
  loading?: boolean
  isVideo?: boolean
  modalCrop?: boolean
  viewMode?: 0 | 1 | 2 | 3
  initialAspectRatio?: number
  aspectRatio?: number
  minCropBoxHeight?: number
  minCropBoxWidth?: number
  cropBoxResizable?: boolean
  withButtonPreloader?: boolean
}

const MediaUploadComponent: React.FC<IProps> = (props) => {
  const classes = useStyles()

  const [cropper, setCropper] = useState<Cropper>()
  const [file, setFile] = useState<string | undefined>()
  const [isVideo, setIsVideo] = useState<boolean>(false)
  const [preview, setPreview] = useState<string | undefined>()
  const [actualImage, setActualImage] = useState<string | undefined>(undefined)
  const [innerLoading, setInnerLoading] = useState(false)

  const temporaryMediaFile: MediaFileMetadata | undefined = props.temporaryMediaFile

  const getCropData = () => {
    if (typeof cropper !== 'undefined') {
      setInnerLoading(true)
      const img = cropper.getCroppedCanvas().toDataURL('image/jpeg')

      cropper.getCroppedCanvas().toBlob((blob) => {
        if (blob) {
          // Convert Blob to File with unique filename and MIME type
          const filename = `thumbnail_${Date.now()}.jpg`
          const file = new File([blob], filename, { type: 'image/jpeg' })
          props.getMediaData && props.getMediaData({ file })
        }
        setInnerLoading(false)
      }, 'image/jpeg')

      setPreview(img)
      setFile(undefined)
    }
  }

  const onChangeImage = (imageUrl: string | undefined) => {
    setFile(imageUrl)
  }

  const onChangeVideo = (video: string) => {
    setFile(video)
    fetch(video)
      .then(res => res.blob())
      .then(blob => props.getMediaData && props.getMediaData({ file: blob }))
    setPreview(video)
    setIsVideo(true)
    setFile(undefined)
  }

  const handleCropped = (instance: Cropper) => {
    setCropper(instance)
  }

  const deleteImage = () => {
    setFile(undefined)
    setPreview(undefined)
    setIsVideo(false)
    setActualImage(undefined)
    props.setMediaFile && props.setMediaFile(undefined)
  }

  useEffect(() => {
    temporaryMediaFile && props.setMediaFile && props.setMediaFile(temporaryMediaFile.directLink, temporaryMediaFile.mediaFileId, temporaryMediaFile.mimeType)
    temporaryMediaFile && (temporaryMediaFile.mimeType.includes('video') ? setIsVideo(true) : setIsVideo(false))
  }, [temporaryMediaFile])

  useEffect(() => {
    setActualImage(props.file as string)
  }, [props.file])

  useEffect(() => {
    props.isUploaded && setPreview(undefined)
  }, [props.isUploaded])

  return (
    <>
      {props.onlyUploadButton && (
        <MediaCropperComponent
          viewMode={props.viewMode}
          mediaFile={file}
          preview={preview}
          modalCrop={props.modalCrop}
          loading={props.loading}
          mediaFileType={props.isVideo ? 'video' : 'image'}
          getCropData={getCropData}
          handleCropped={handleCropped}
          deleteImage={deleteImage}
          onChangeImage={onChangeImage}
          onChangeVideo={onChangeVideo}
          withButtonPreloader={props.withButtonPreloader}
          allowVideoUpload={props.allowVideoUpload}
          allowImageUpload={props.allowImageUpload}
          minCropBoxHeight={props.minCropBoxHeight}
          minCropBoxWidth={props.minCropBoxWidth}
          containerHeight={props.containerHeight}
          containerWidth={props.containerWidth}
          cropBoxResizable={props.cropBoxResizable}
          initialAspectRatio={props.initialAspectRatio}
          aspectRatio={props.aspectRatio}
        />)}
      {!props.onlyUploadButton && (
        <Paper className={classes.root}>
          {!innerLoading && !props.loading
            ? (
              <>
                {!file && !(props.isVideo || isVideo) && (
                  <Box display='flex' justifyContent='center'>
                    <ImageComponent
                      className={classes.image}
                      src={preview || actualImage}
                      handleFallback={() => setActualImage(undefined)}
                      fallback='/src/shared/assets/images/icon-img-placeholder.svg'
                    />
                  </Box>)}
                {!file && (props.isVideo || isVideo) && (
                  <Box display='flex' justifyContent='center'>
                    <VideoComponent
                      src={preview || actualImage}
                      fallbackThumb='/src/shared/assets/images/icon-img-placeholder.svg'
                      height={450}
                      width='100%'
                      controls
                    />
                  </Box>)}
              </>
            )
            : <Skeleton variant="rectangular" sx={{ height: props.containerHeight || 400, width: props.containerWidth || '100%' }} />}
          <Box className={classes.imgDesc}>
            <Box>
              <MediaCropperComponent
                loading={props.loading}
                mediaFile={file}
                getCropData={getCropData}
                handleCropped={handleCropped}
                deleteImage={deleteImage}
                onChangeImage={onChangeImage}
                onChangeVideo={onChangeVideo}
                hasActualImage={!!actualImage}
                hasPreviewImage={!!preview}
                allowVideoUpload={props.allowVideoUpload}
                allowImageUpload={props.allowImageUpload}
                cropBoxResizable={props.cropBoxResizable}
              />
            </Box>
            <Box>
              {!props.allowVideoUpload &&<Typography variant="body2" sx={{ pt: 1 }}>max. Bildgröße: 16MB</Typography>}
              {props.allowVideoUpload &&<Typography variant="body2" sx={{ pt: 1 }}>max. Dateigröße: 50MB</Typography>}
            </Box>
          </Box>
        </Paper>)}
    </>

  )
}

export default MediaUploadComponent
